/**
 * "Duplicates" on the accounts list: groups of accounts with the same name, sorted into what to
 * do about them (merge, delete the empty copies, or keep as branches). Read-only: the cleanup
 * happens in zInspector (it has a property merge), with this list as the to-do.
 */
import { Copy } from "lucide-react";
import { useGetList } from "ra-core";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import type { ZcrmAccount } from "../providers/zinspector/accounts";
import {
  findDuplicateAccounts,
  type DuplicateKind,
} from "../providers/zinspector/duplicates";

const KIND_HELP: Record<
  DuplicateKind,
  { variant: "error" | "warning" | "neutral"; help: string }
> = {
  merge: {
    variant: "error",
    help: "Several copies have history: merge them in zInspector.",
  },
  "empty copies": {
    variant: "warning",
    help: "At most one copy has history: the others can be deleted.",
  },
  branches: {
    variant: "neutral",
    help: "Different markets: probably real branches. Check, then keep.",
  },
};

export const AccountDuplicates = () => {
  const [open, setOpen] = useState(false);
  const { data: accounts = [] } = useGetList<ZcrmAccount>(
    "companies",
    {
      pagination: { page: 1, perPage: 10_000 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: open },
  );
  const groups = useMemo(() => findDuplicateAccounts(accounts), [accounts]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Copy /> Duplicates
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Duplicate accounts</DialogTitle>
          <DialogDescription>
            Accounts whose names match once legal forms ("LLC", "Inc") and
            punctuation are ignored. {groups.length} group
            {groups.length === 1 ? "" : "s"}.
          </DialogDescription>
        </DialogHeader>
        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">No duplicates found.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {groups.map((group) => (
              <li key={group.key} className="rounded-md border p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">{group.accounts[0].name}</span>
                  <Badge variant={KIND_HELP[group.kind].variant}>
                    {group.kind}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {KIND_HELP[group.kind].help}
                </p>
                <ul className="mt-2 divide-y text-sm">
                  {group.accounts.map((account) => (
                    <li
                      key={account.id}
                      className="flex justify-between gap-3 py-1.5"
                    >
                      <Link
                        to={`/companies/${account.id}/show`}
                        className="underline-offset-2 hover:underline"
                        onClick={() => setOpen(false)}
                      >
                        {account.name}
                      </Link>
                      <span className="text-muted-foreground text-xs">
                        {[
                          account.market,
                          account.accounting_id,
                          `${account.nb_contacts} contacts`,
                          `${account.open_deals} open deals`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
};
